import { Link } from "react-router-dom";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { CALCULATORS, ICONS } from "@/data/calculators";

function InfoShell({ title, description, children, testid }) {
  return (
    <>
      <Nav />
      <Seo title={`${title} — Curiosímetro`} description={description} />
      <main className="mx-auto max-w-3xl px-5 pb-20 pt-14 sm:px-8" data-testid={testid}>
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-primary">Curiosímetro</p>
          <h1 className="mt-4 font-serif text-4xl font-medium tracking-tight sm:text-5xl">{title}</h1>
        </Reveal>
        <div className="mt-10 space-y-10">{children}</div>
      </main>
      <Footer />
    </>
  );
}

function Block({ title, children }) {
  return (
    <Reveal>
      <section>
        <h2 className="font-serif text-2xl tracking-tight">{title}</h2>
        <div className="mt-3 space-y-3 text-base leading-relaxed text-muted-foreground">{children}</div>
      </section>
    </Reveal>
  );
}

export function PrivacidadPage() {
  return (
    <InfoShell
      title="Privacidad y cookies"
      description="Qué guarda Curiosímetro, qué cookies usan los anuncios y cómo cambiar tu decisión en cualquier momento."
      testid="privacidad-page"
    >
      <Block title="Qué se guarda de ti">
        <p>
          Curiosímetro no pide registro y no guarda tus cálculos: los datos que mueves en cada
          calculadora nunca salen de tu navegador. Lo único que se registra de forma anónima es
          un contador de usos por calculadora y tu decisión sobre las cookies, sin identificarte.
        </p>
      </Block>
      <Block title="Cookies publicitarias (Google AdSense)">
        <p>
          Si aceptas el banner de cookies, Google AdSense puede usar cookies y tecnologías
          similares para servir anuncios, medir resultados y combatir el fraude. Si rechazas, no
          se cargan. Puedes consultar cómo usa Google los datos en{" "}
          <a
            href="https://policies.google.com/technologies/ads"
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-dotted underline-offset-2 hover:text-primary"
          >
            policies.google.com/technologies/ads
          </a>
          .
        </p>
      </Block>
      <Block title="Cambiar tu decisión">
        <p>
          En el pie de cualquier página encontrarás el enlace «Preferencias de cookies»: bórrala
          y el banner volverá a aparecer para que elijas de nuevo.
        </p>
      </Block>
      <Block title="Naturaleza de los resultados">
        <p>
          Todos los resultados son estimaciones divulgativas basadas en medias científicas. No
          constituyen consejo médico ni diagnóstico.
        </p>
      </Block>
    </InfoShell>
  );
}

export function MetodologiaPage() {
  return (
    <InfoShell
      title="Metodología"
      description="Cómo calcula Curiosímetro cada cifra: las medias que usamos, las fórmulas que mostramos y lo que no hacemos."
      testid="metodologia-page"
    >
      <Block title="Cómo calculamos">
        <p>
          Cada calculadora combina tu entrada con una media pública y divulgativa: años de 365,25
          días, pulsaciones por minuto, centímetros de crecimiento al mes. La fórmula exacta se
          muestra abierta al pie de cada página, junto con las fuentes y los factores que pueden
          hacer variar el resultado.
        </p>
      </Block>
      <Block title="Qué suponemos">
        <p>
          Los cuerpos reales no son promedios: la esperanza de vida es configurable, el pulso
          cambia con el estado físico y el cabello crece distinto según genética y estación.
          Tratamos cada resultado como una estimación con margen, no como una cifra exacta.
        </p>
      </Block>
      <Block title="Qué no hacemos">
        <p>
          No damos consejo médico, no guardamos tus datos personales y no compartimos resultados
          con terceros. Si un número te preocupa, la persona adecuada para interpretarlo es un
          profesional sanitario, no una calculadora.
        </p>
      </Block>
      <Block title="Las seis calculadoras">
        <ul className="list-none space-y-2">
          {CALCULATORS.map((c) => {
            const Icon = ICONS[c.icon];
            return (
              <li key={c.slug}>
                <Link
                  to={`/${c.slug}`}
                  data-testid={`metodologia-link-${c.slug}`}
                  className="inline-flex items-center gap-2 font-serif text-lg transition-colors hover:text-primary"
                >
                  <Icon className="h-4 w-4" style={{ color: c.accent }} />
                  {c.title}
                </Link>
              </li>
            );
          })}
        </ul>
      </Block>
    </InfoShell>
  );
}
